// src/utils/textCatalog.js

/**
 * Como um texto se APRESENTA quando a catalogação está incompleta, e como
 * gênero e assuntos viram chips.
 *
 * Mora aqui, e não dentro de um componente, porque duas telas mostram os mesmos
 * campos do mesmo texto: a estante (TextCardsForm.vue) e o painel de metadados
 * do leitor (TextDisplayer.vue). A regra de lacuna escrita nos dois lugares
 * divergiria em silêncio — uma tela diria "Autor não informado" e a outra
 * deixaria a linha em branco, e nada acusaria a diferença.
 *
 * É o mesmo motivo do utils/textCover.js, que guarda a capa desenhada.
 */

import { toTitleCase } from "@/utils/stringUtils";

// Um texto do cadastro rápido pode nascer sem autor, gênero ou assunto. Escreve
// "Não informado" por extenso em vez do travessão da tabela: fora de uma
// coluna com cabeçalho, o campo em branco lê como falha de carregamento.
export const NOT_INFORMED = "Não informado";

/**
 * O rótulo de ausência nomeia o CAMPO. Não há cabeçalho de coluna dizendo o que
 * aquela linha é: um "Não informado" solto embaixo do título poderia ser autor,
 * gênero ou qualquer outra coisa.
 */
export function catalogLabel(value, fieldLabel) {
  const filled = toTitleCase(value);
  if (filled) return filled;
  return fieldLabel
    ? `${fieldLabel} ${NOT_INFORMED.toLowerCase()}`
    : NOT_INFORMED;
}

/**
 * Chips de classificação: gênero primeiro, assuntos depois — o gênero é a
 * classificação mais ampla e serve de âncora quando o texto tem muitos
 * assuntos.
 *
 * Campo vazio vira chip apagada dizendo o que falta, em vez de sumir: sem ela,
 * o texto sem gênero fica visualmente igual ao sem assunto, e "não informado"
 * tem de aparecer POR CAMPO para dizer qual dos dois está em branco.
 *
 * Cada chip carrega o `id` do que ela nomeia, para quem puder abrir o card
 * correspondente (o painel do leitor abre; a estante não). Quem não usa,
 * ignora.
 *
 * E carrega o `field` — "genre" ou "topic" —, que diz de QUAL campo ela fala.
 * O `kind` não serve para isso: a chip de ausência tem kind "empty" nos dois
 * casos, e sem o `field` quem quisesse separar gênero de assunto teria de ler o
 * texto do rótulo. O painel do leitor separa, para dar a cada campo o seu botão
 * de editar; a estante mostra tudo numa fileira só e ignora este campo.
 *
 * `limit` corta o excedente em "+N" — restrição de altura de card, não regra de
 * catalogação: sem ele, mostra tudo.
 */
export function catalogChips(text, { limit = Infinity } = {}) {
  const all = [];

  if (text?.categoryName) {
    all.push({
      kind: "genre",
      field: "genre",
      name: text.categoryName,
      id: text.categoryId,
    });
  } else {
    all.push({
      kind: "empty",
      field: "genre",
      name: `Gênero ${NOT_INFORMED.toLowerCase()}`,
    });
  }

  const topicNames = Array.isArray(text?.topicNames) ? text.topicNames : [];
  const topicIds = Array.isArray(text?.topicIds) ? text.topicIds : [];
  const topics = topicNames
    .map((name, idx) => ({
      kind: "topic",
      field: "topic",
      name,
      id: topicIds[idx],
    }))
    .filter((chip) => Boolean(chip.name));

  if (topics.length) {
    all.push(...topics);
  } else {
    all.push({
      kind: "empty",
      field: "topic",
      name: `Assunto ${NOT_INFORMED.toLowerCase()}`,
    });
  }

  return {
    visible: all.slice(0, limit),
    extra: Math.max(0, all.length - limit),
  };
}

/**
 * A fonte, na mesma linguagem das chips — mas SEPARADA delas, e é por isso que
 * é função à parte: ela é procedência, não classificação (diz de onde o texto
 * veio, não do que ele trata), e quem a mostra lhe dá uma linha só sua. Só o
 * painel do leitor a usa; o card da estante não tem fonte.
 *
 * Ausente, segue a regra dos outros campos e vira chip apagada nomeando o que
 * falta — "informadA", porque fonte é feminino e o NOT_INFORMED masculino que
 * serve a gênero e assunto não serve aqui.
 */
export function sourceChip(text) {
  return text?.source
    ? { kind: "source", name: text.source }
    : { kind: "empty", name: "Fonte não informada" };
}
